import Container from "@/components/Container/Container.tsx";
import { HolderForm } from "@/components/HolderForm/HolderForm.tsx";
import { PaymentForm } from "@/components/Payment/PaymentForm/PaymentForm.tsx";
import { ReservationSidebar } from "@/components/ReservationSidebar/ReservationSidebar.tsx";
import { Stepper } from "@/components/Stepper/Stepper.tsx";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { useScrollToTop } from "@/helpers/scrollToTop.ts";
import { PaymentProviderEnum, PaymentType } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useTryHolderFormSubmit } from "@/pages/BookingEngine/hooks/useTryHolderFormSubmit.tsx";
import { ReservationHolderInformationProps } from "@/pages/ReservationHolderInformation/ReservationHolderInformationProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

export const HolderAndPaymentInfo = ({
  holderFormData,
  paymentFormData,
}: ReservationHolderInformationProps) => {
  const scrollToTop = useScrollToTop();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const { paymentEnabled, paymentType, hasEmbeddedCardForm } = useGetOrganization();
  const { setPage, setReservationHolderData, calculatedCartData, setPaymentData } = useEngineStore(
    useShallow((state) => ({
      setPage: state.setPage,
      setReservationHolderData: state.setReservationHolderData,
      calculatedCartData: state.calculatedCartData,
      setPaymentData: state.setPaymentData,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);

  const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;

  const shouldShowPaymentForm = useMemo(
    () =>
      !isForcedInquiry && paymentEnabled && paymentType === PaymentProviderEnum.WsPayPer && isCardGuarantee,
    [isForcedInquiry, paymentEnabled, paymentType, isCardGuarantee],
  );

  const tryHolderFormSubmit = useTryHolderFormSubmit(holderFormData, paymentFormData);

  const { register, errors, setValue, watch, trigger, setFocus } = holderFormData;
  const { onSubmitPaymentForm } = paymentFormData;

  const handleContinue = async () => {
    const result = await tryHolderFormSubmit();
    if (!result) return;

    setReservationHolderData(result.holder);
    if (!isForcedInquiry && hasEmbeddedCardForm && isCardGuarantee && result.payment) {
      onSubmitPaymentForm(result.payment);
    } else {
      setPaymentData(undefined);
    }
    setPage(AppPage.ReservationOverview);
    handleCloseCartDrawer();
    scrollToTop();
  };

  return (
    <div>
      <Stepper onTryNext={tryHolderFormSubmit} />
      <main className="holderWrap o-page">
        <div className="o-page__inner">
          <section className="c-holder-information-options-wrapper">
            <Container>
              <div className="holderOptions c-holder-information-options">
                <div className="c-holder-information-options__wrap">
                  <div className="c-holder-information-options__options">
                    <HolderForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      trigger={trigger}
                      setFocus={setFocus}
                    />
                    {shouldShowPaymentForm && (
                      <div className="c-holder-information-options__payment">
                        <PaymentForm paymentFormData={paymentFormData} holderFormData={holderFormData} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="c-holder-information-options__summary">
                  <div className="c-holder-information-options__summary-inner">
                    <ReservationSidebar hideRemoveRate onContinue={handleContinue} />
                  </div>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </main>
    </div>
  );
};
